import { View, Pressable } from "react-native"
import { Link, type Href } from "expo-router"

import { Text, ActivityIndicator, Header, List } from "@src/components"
import { sharedStyles, trpc, type RouterOutputs, dayjs } from "@src/utils"
import { ar } from "@src/i18n"

type AdjustmentItem = RouterOutputs["inventory"]["listAdjustments"]["items"][number]

function formatWeight(grams: number) {
  const sign = grams < 0 ? "-" : ""
  const abs = Math.abs(grams)
  if (abs >= 1000) {
    return `${sign}${(abs / 1000).toFixed(2).replace(/\.00$/, "")} ${ar.kg}`
  }
  return `${sign}${abs} ${ar.gms}`
}

function AdjustmentCard({ item }: { item: AdjustmentItem }) {
  const isWeight = item.productType === "WEIGHT_PRODUCT" || item.productType === "WEIGHT_DEFINED_PRODUCT"
  const displayQty = isWeight ? formatWeight(Math.abs(item.quantity)) : Math.abs(item.quantity)
  const beforeQty = isWeight ? formatWeight(item.quantityBefore ?? 0) : item.quantityBefore
  const afterQty = isWeight ? formatWeight(item.quantityAfter ?? 0) : item.quantityAfter

  const title = ar.ADJUSTMENT
  const isPositive = item.quantity > 0

  const href: Href = `/dashboard/inventory/storage/1/product/${item.productId}`

  return (
    <Link href={href} asChild>
      <Pressable className="bg-white border-b border-gray-100 p-4 flex-row justify-between items-start active:opacity-70">
        <View className="flex-1">
          <View className="flex-row items-center gap-2 mb-1">
            <View className={`px-2 py-0.5 rounded ${isPositive ? "bg-green-100" : "bg-red-100"}`}>
              <Text className={`text-xs font-medium ${isPositive ? "text-green-800" : "text-red-800"}`}>{title}</Text>
            </View>
            <Text className="text-gray-400 text-xs">
              {dayjs.unix(item.createdAt).fromNow()} - {dayjs.unix(item.createdAt).format("DD/MM/YYYY hh:mm A")}
            </Text>
          </View>

          {item.productName ? <Text className="text-gray-900 text-sm font-semibold mb-1">{item.productName}</Text> : null}

          {item.reason ? (
            <Text className="text-gray-600 text-sm mb-1">
              {ar.adjustStockReason}: {item.reason}
            </Text>
          ) : null}

          <Text className="text-gray-500 text-xs">
            {beforeQty} → {afterQty}
          </Text>
        </View>

        <View className="items-end">
          <Text className={`font-bold text-base ${item.quantity > 0 ? "text-green-600" : item.quantity < 0 ? "text-red-600" : "text-gray-600"}`}>
            {item.quantity > 0 ? "+" : item.quantity < 0 ? "-" : ""}
            {displayQty}
          </Text>
          <Text className="text-gray-400 text-xs">
            {afterQty} {ar.balance}
          </Text>
        </View>
      </Pressable>
    </Link>
  )
}

export default function AdjustmentsScreen() {
  const query = trpc.inventory.listAdjustments.useInfiniteQuery({ limit: 25 }, { getNextPageParam: (lastPage) => lastPage.nextCursor })

  const items = query.data?.pages.flatMap((page) => page.items) || []

  if (query.isLoading) {
    return (
      <>
        <Header title={ar.adjustmentHistory} />
        <ActivityIndicator size="large" color="#4B5563" className="mt-10" />
      </>
    )
  }

  return (
    <>
      <Header title={ar.adjustmentHistory} />
      <List
        data={items}
        keyExtractor={(item) => `${item.id}`}
        renderItem={({ item }) => <AdjustmentCard item={item} />}
        onEndReached={() => {
          if (query.hasNextPage && !query.isFetchingNextPage) query.fetchNextPage()
        }}
        onEndReachedThreshold={0.5}
        onRefresh={query.refetch}
        ListFooterComponent={query.isFetchingNextPage ? <ActivityIndicator size="small" className="p-4" /> : null}
        ListEmptyComponent={
          <View className="p-8 items-center">
            <Text className="text-gray-400">{ar.noAdjustments}</Text>
          </View>
        }
        contentContainerStyle={sharedStyles.contentContainer}
        style={{ flex: 1 }}
      />
    </>
  )
}
